<template>
  <div class="pointer-events-none fixed inset-x-0 bottom-0 z-[1010] flex justify-center p-[14px] max-md:pe-[70px]">
    <div class="promo-banner-bar pointer-events-auto relative w-full max-w-[900px]">
      <div
        class="relative flex items-center gap-4 overflow-hidden rounded-2xl border border-[color-mix(in_srgb,var(--white)_8%,transparent)] border-t-[color-mix(in_srgb,var(--white)_16%,transparent)] bg-[#0d0d0f]/90 p-[14px_16px] shadow-[0_16px_40px_rgba(0,0,0,0.45)] backdrop-blur-2xl"
      >
        <LayoutPromoCodeConfetti :shots="shots" />

        <div class="flex min-w-0 flex-1 flex-wrap items-center gap-3.5 gap-y-2">
          <span
            class="shrink-0 whitespace-nowrap rounded-md border border-[#ff6a2e]/30 bg-[#ff6a2e]/[0.14] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.6px] text-[#ff6a2e]"
          >
            {{ promoCode.badge }}
          </span>
          <div class="min-w-0">
            <div class="font-anton text-[clamp(16px,2.1vw,20px)] font-normal leading-[1.2] tracking-[-0.01em] text-title-color">
              {{ promoCode.titlePrefix }} <span class="text-[#ff6a2e]">{{ promoCode.titleHighlight }}</span>
            </div>
            <div class="promo-banner-sub font-medium text-[12.5px] mt-px text-paragraph-color">
              {{ promoCode.subText }}
            </div>
          </div>
        </div>

        <div class="flex shrink-0 items-center gap-2">
          <button
            type="button"
            class="flex items-center gap-2 rounded-[10px] border border-[color-mix(in_srgb,var(--white)_14%,transparent)] bg-[color-mix(in_srgb,var(--white)_5%,transparent)] px-3 py-2 text-white transition-[background-color,border-color] duration-150 ease-out hover:border-[color-mix(in_srgb,var(--white)_25%,transparent)] hover:bg-[color-mix(in_srgb,var(--white)_9%,transparent)] active:scale-[0.96]"
            :aria-label="`Copy promo code ${promoCode.code}`"
            @click="copyCode"
          >
            <span class="font-anton text-[15px] font-normal tracking-[1.2px]">{{ promoCode.code }}</span>
            <span class="flex size-[22px] shrink-0 items-center justify-center">
              <icon-promo-check v-if="copied" class="text-[16px] text-[#4ade80] [&_path]:!fill-none" />
              <icon-promo-copy v-else class="text-[15px] text-[color-mix(in_srgb,var(--white)_55%,transparent)] [&_path]:!fill-none" />
            </span>
          </button>

          <button
            type="button"
            class="flex size-[30px] shrink-0 items-center justify-center rounded-full text-[color-mix(in_srgb,var(--white)_40%,transparent)] transition-colors duration-150 ease-out hover:bg-[color-mix(in_srgb,var(--white)_8%,transparent)] hover:text-[color-mix(in_srgb,var(--white)_85%,transparent)] active:scale-[0.88]"
            aria-label="Minimize promo banner"
            @click="emit('minimize')"
          >
            <icon-promo-close class="text-[13px]" />
          </button>
        </div>
      </div>

      <Transition name="promo-toast">
        <div
          v-if="showToast"
          class="promo-toast absolute bottom-[calc(100%+10px)] right-[16px] whitespace-nowrap rounded-lg border border-[color-mix(in_srgb,var(--white)_12%,transparent)] bg-[#1a1a1d] px-3 py-1.5 text-[12px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.4)]"
        >
          Code copied
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PromoCodeFeature } from "../../../types/templateConfiguration";
import type { PromoConfettiShot } from "./PromoCodeConfetti.vue";

const props = defineProps<{ promoCode: PromoCodeFeature }>();
const emit = defineEmits<{ minimize: [] }>();

const { copied, showToast, copyCode } = usePromoCodeClipboard(props.promoCode.code);

// paint-splat decoration positions, tuned for the wide bottom-bar layout
const shots: PromoConfettiShot[] = [
  { top: 18, size: 10, travel: 620, arc: 20, delay: 0.4, colorFrom: "#ffb27a", colorMid: "#ff6a2e", colorTo: "#c94500", glow: "rgba(255,106,46,0.75)", splatTop: -10, splatRight: 250, splatSize: 58 },
  { top: 46, size: 9, travel: 560, arc: 34, delay: 0.75, colorFrom: "#fff2a8", colorMid: "#ffd23f", colorTo: "#b88900", glow: "rgba(255,210,63,0.75)", splatTop: 18, splatRight: 175, splatSize: 46 },
  { top: 8, size: 11, travel: 700, arc: 12, delay: 1.1, colorFrom: "#ff9fd6", colorMid: "#ff3fa4", colorTo: "#a30057", glow: "rgba(255,63,164,0.75)", splatTop: -14, splatRight: 40, splatSize: 62 },
];
</script>

<style scoped>
.promo-banner-bar {
  animation: promo-banner-slide-up 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
.font-anton,
.font-anton * {
  font-family: "Anton", Impact, sans-serif;
}
@keyframes promo-banner-slide-up {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
.promo-toast-enter-active {
  transition:
    opacity 180ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
}
.promo-toast-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
@media (max-width: 480px) {
  .promo-banner-sub {
    display: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .promo-banner-bar {
    animation: none;
    transition: opacity 200ms ease;
  }
}
</style>
